import { IsoDateTime, Uuid } from "@secretaa/http-contract";
import { z } from "zod";
import { isCurrency } from "../currencies.ts";

// Giới hạn parser tạm thời để bảo vệ contract; D.5 phải chốt operational budgets trước runtime.
export const ContractBounds = { nodes: 128, criteria: 64, bindings: 128, text: 8000 } as const;
export const Key = z.string().regex(/^[a-z][a-z0-9_.-]{0,95}$/);
export const Digest = z.string().regex(/^[a-f0-9]{64}$/);
export const Version = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const Ref = z.object({ id: Uuid, version: Version }).strict();
export const Scope = z.object({ workspaceId: Uuid, projectId: Uuid.nullable() }).strict();
export const Identity = { schemaVersion: z.literal(1), id: Uuid, version: Version, scope: Scope };
export const Actor = z.object({ kind: z.enum(["USER", "AUTOMATION"]), id: Uuid }).strict();
export const Label = z.object({ vi: z.string().trim().min(1).max(240), en: z.string().trim().min(1).max(240) }).strict();
export const Budget = z.object({ currency: z.string().regex(/^[A-Z]{3}$/).refine(isCurrency, "CURRENCY_UNSUPPORTED"), maxMinor: z.string().regex(/^(0|[1-9]\d{0,17})$/) }).strict();
export const Effect = z.enum(["READ", "DATA_WRITE", "EXTERNAL_WRITE"]);
export const Pointer = z.string().max(512).regex(/^(\/(?:[^~/]|~[01])*)*$/).refine(value =>
  !value.split("/").some(part => ["__proto__", "prototype", "constructor"].includes(part.replace(/~1/g, "/").replace(/~0/g, "~"))), "UNSAFE_POINTER");
export const Binding = z.object({ source: z.enum(["INPUT", "STEP_OUTPUT"]), key: Key, path: Pointer, targetPath: Pointer }).strict();
export const Bindings = z.array(Binding).max(ContractBounds.bindings).superRefine((bindings, ctx) => {
  for (let i = 0; i < bindings.length; i++) for (let j = i + 1; j < bindings.length; j++) {
    const a = bindings[i]!.targetPath, b = bindings[j]!.targetPath;
    if (a === b || a.startsWith(b + "/") || b.startsWith(a + "/"))
      ctx.addIssue({ code: "custom", path: [j, "targetPath"], message: "OVERLAPPING_BINDINGS" });
  }
});
export const ArtifactRef = z.object({ ref: Ref, workspaceId: Uuid, contractRef: Ref }).strict();
export const Expiry = IsoDateTime;
export type Ref = z.infer<typeof Ref>;
export type Scope = z.infer<typeof Scope>;
export type Diagnostic = { code: string; path: string; relatedId?: string };
export const sameRef = (a: Ref, b: Ref) => a.id === b.id && a.version === b.version;
export const sameScope = (a: Scope, b: Scope) => a.workspaceId === b.workspaceId && a.projectId === b.projectId;
